import Joi from 'joi'
import clsx from 'clsx'
import Avatar from 'react-avatar'
import { useRef, useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import Checkbox from '../../../../UI/Checkbox'
import Btn from '../../../../UI/Btn'
import TextField from '../../../../UI/TextField'
import SelectField from '../../../../UI/SelectField'
import TabBar from '../../../../UI/TabBar'
import AuthAvatar from '../../../../UI/AuthAvatar'
import BaseModal from '../../../../UI/BaseModal/BaseModal'
import RecordComments from '../../../../UI/RecordComments'
import ReminderSettings from '../../../../UI/ReminderSettings'
import { schemaOptions } from '../../../../shared/i18n'
import http from '../../../../shared/http'
import { types } from '../../../../shared/eventEmitter'
import { formatVienna } from '../../../../shared/datetime'
import { stateLabel, NAME_PATTERN } from '../../../../shared/helpers'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import { TApi, StateOptions, genderOption, TLanguages, TRoles, RECORD_TYPES } from '../../../../shared/const'

import styles from './style.module.scss'

const schema = Joi.object({
	firstName: Joi.string()
		.min(2)
		.max(26)
		.pattern(NAME_PATTERN, 'name')
		.required(),
	lastName: Joi.string()
		.min(2)
		.max(26)
		.pattern(NAME_PATTERN, 'name')
		.required(),
	email: Joi.string().email({ tlds: false }).required(),
	gender: Joi.string().required(),
	salutation: Joi.string().required(),
	password: Joi.string().allow(''),
	role: Joi.string().valid(TRoles.ADMIN, TRoles.FEDERAL_ADMIN, TRoles.ALUMNI).required().messages({
		'any.only': 'Invalid role selected!'
	}),
	// Bundesweite Rollen (Bundesadmin, Alumni #213) tragen kein Bundesland —
	// bei Alumni wäre es sonst die Grenze ihres Blicks.
	state: Joi.string().when('role', {
		is: Joi.valid(TRoles.FEDERAL_ADMIN, TRoles.ALUMNI),
		then: Joi.valid(null).allow(''), // allow null or empty string for federal admin
		otherwise: Joi.required() // require state for non-federal admin
	}),
	organizationName: Joi.string().when('role', {
		is: Joi.valid(TRoles.FEDERAL_ADMIN, TRoles.ALUMNI),
		then: Joi.valid(null).allow(''), // allow null or empty string for federal admin
		otherwise: Joi.required() // require organization name for non-federal admin
	}),
	manageStateAdmins: Joi.boolean().default(false),
	manageDatabaseReset: Joi.boolean().default(false),
	manageFederalAdmins: Joi.boolean().default(false)
})

// Ohne Bundesland: Bundesadmin und Alumni (#213).
const NATIONWIDE_ROLES = [TRoles.FEDERAL_ADMIN, TRoles.ALUMNI]

/*
 * Die drei Zusatzfunktionen eines Bundesadmins.
 *
 * Sie stehen hier mit ihrer Erklärung beisammen, weil ein Häkchen allein nicht
 * sagt, was es bewirkt: „Verwaltung Datenbank Reset" klingt nach einer
 * Einstellung und schaltet in Wahrheit einen Eingriff frei, der den ganzen
 * Datenbestand betrifft. Wer die Rechte vergibt, soll das lesen können, ohne
 * im Handbuch nachzuschlagen.
 */
const PERMISSIONS = [
	{
		name: 'manageStateAdmins',
		label: 'Manage State Admins',
		hint: 'May create, edit and delete state admins.'
	},
	{
		name: 'manageFederalAdmins',
		label: 'Manage Federal Admins',
		hint: 'May create, edit and delete federal admins — and may choose the role when creating an administrator.'
	},
	{
		name: 'manageDatabaseReset',
		label: 'Manage Database Reset',
		hint: 'Unlocks the school year reset under Settings › System: the data is exported and cleared. Teachers, administrators, supporters, school master data and all settings are kept.'
	}
]

/*
 * Die drei Fragen an einen Administrator, je eine je Reiter: Wer ist das
 * (Person) · Was darf er (Rolle und Zusatzfunktionen) · Wofuer ist er
 * zustaendig (Organisation und Bundesland).
 *
 * Vorher standen alle drei Abschnitte untereinander in einem Fenster, das auf
 * einem 13-Zoll-Bildschirm scrollte: Wer die Zusatzfunktionen eines
 * Bundesadmins setzte, sah den Namen der Person dabei nicht mehr. Deshalb steht
 * die Person jetzt im Kopf ueber den Reitern und bleibt beim Wechseln stehen.
 */
const TAB_KEYS = {
	PERSON: 'person',
	ROLE: 'role',
	RESPONSIBILITY: 'responsibility',
	/*
	 * Seit 14.10.2026: die Erinnerungen per E-Mail dieses Kontos und der
	 * Verlauf (wer hat sie wann geändert). Beides gibt es erst, wenn das Konto
	 * existiert — beim Anlegen fehlen die Reiter.
	 */
	REMINDERS: 'reminders',
	COMMENTS: 'comments'
}

// Die Reiter, die zum Formular des Zugangs gehören (Person, Rolle,
// Zuständigkeit) — nur dort steht der Knopf „Speichern" des Formulars.
const FORM_TABS = [TAB_KEYS.PERSON, TAB_KEYS.ROLE, TAB_KEYS.RESPONSIBILITY]

// Welches Feld in welchem Reiter steht. Gebraucht fuer die Ampel an der
// Reiterleiste und um beim Speichern zum beanstandeten Feld zu springen.
const TAB_FIELDS = {
	[TAB_KEYS.PERSON]: ['salutation', 'gender', 'firstName', 'lastName', 'email', 'password'],
	[TAB_KEYS.ROLE]: ['role', 'manageStateAdmins', 'manageFederalAdmins', 'manageDatabaseReset'],
	[TAB_KEYS.RESPONSIBILITY]: ['organizationName', 'state'],
	[TAB_KEYS.REMINDERS]: [],
	[TAB_KEYS.COMMENTS]: []
}

function AddAdmin(props) {
	const permissions = props.permissions

	const { t, i18n } = useTranslation()
	const {
		reset,
		control,
		setValue,
		trigger,
		register,
		handleSubmit,
		watch,
		formState: { errors, dirtyFields }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		})
	})

	const [id, setId] = useState(null)
	const [isLoading, setLoading] = useState(false)
	const [onDone, setOnDone] = useState(null)
	// Der geoeffnete Datensatz, wie ihn die Liste mitgibt. Gebraucht fuer den
	// Kopf des Fensters — Profilbild, E-Mail und letzte Anmeldung stehen in
	// keinem Eingabefeld.
	const [admin, setAdmin] = useState(null)
	const [tab, setTab] = useState(TAB_KEYS.PERSON)
	/*
	 * Darf die angemeldete Person den Zugang selbst bearbeiten (Recht zur
	 * Rolle, siehe Admins/index.jsx `canManage`)? Ohne das Recht öffnet die
	 * Liste das Fenster trotzdem — für die Erinnerungen, die jeder Bundesadmin
	 * für alle einstellen darf. Dann gibt es nur die Reiter „Erinnerungen" und
	 * „Kommentare", das Formular des Zugangs bleibt ausgeblendet.
	 */
	const [canEdit, setCanEdit] = useState(true)
	// Ungespeicherte Erinnerungen zählen beim Schließen wie ein geändertes Feld.
	const [remindersDirty, setRemindersDirty] = useState(false)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)

	const selectedRole = watch('role')

	/*
	 * Bundesweite Rollen tragen weder Bundesland noch Organisation — das
	 * Schema lässt dort nur leer zu. Wer die Rolle umstellt (etwa ein
	 * Bundesadmin-Konto auf Alumni, #213), hätte sonst einen Wert in einem
	 * Feld, das gar nicht mehr zu sehen ist, und das Speichern scheiterte daran.
	 */
	useEffect(() => {
		if (!NATIONWIDE_ROLES.includes(selectedRole)) return
		setValue('state', '')
		setValue('organizationName', '')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRole])

	// Der Name im Kopf folgt der Eingabe: Wer ihn im Reiter darunter berichtigt,
	// saehe sonst bis zum Speichern weiter den alten oben stehen.
	const name = [watch('firstName'), watch('lastName')].filter(Boolean).join(' ')

	// Die Rolle darf nur wählen, wer Bundesadmins verwalten darf. Für alle
	// anderen entsteht ein Landesadmin — ohne diese Vorbelegung bliebe das
	// Pflichtfeld leer und das Speichern scheiterte an einem Feld, das gar
	// nicht zu sehen ist.
	const canChooseRole = Boolean(permissions?.manageFederalAdmins)

	useEffect(() => {
		if (Object.keys(errors).length) trigger()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localStorage.getItem('i18nextLng')])

	const onSubmit = (form, closeModal) => {
		setLoading(true)

		const finalForm = {
			...form,
			...(NATIONWIDE_ROLES.includes(form.role) && { state: null }),
			// Alumni verwalten nichts — die Häkchen gibt es für sie nicht.
			...(form.role === TRoles.ALUMNI && {
				manageStateAdmins: false,
				manageFederalAdmins: false,
				manageDatabaseReset: false
			})
		}

		const request = id
			? http.put(TApi.ADMIN_STATE_ADMINS_ID(id), finalForm)
			: http.post(TApi.ADMIN_STATE_ADMINS, {
					...finalForm,
					email: form.email.toLowerCase()
			  })

		request
			.then(() => {
				toast.success(`${t('Admin was successfully')} ${id ? t('updated') : t('created')} `)
				setId(null)
				closeModal()
				if (onDone) onDone()
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const handleOpen = (admin = null, onDone = null, options = {}) => {
		setOnDone(() => onDone)
		setAdmin(admin)
		setCanEdit(!admin || options.canEdit !== false)
		setRemindersDirty(false)

		if (!admin) {
			if (!canChooseRole) setValue('role', TRoles.ADMIN)
			/*
			 * Beim Anlegen faengt es mit der Rolle an: Sie ist Pflicht und
			 * entscheidet, welche Felder in der Zustaendigkeit ueberhaupt
			 * verlangt werden. Wer sie nicht waehlen darf, faengt bei der
			 * Person an — dort gibt es fuer ihn nichts zu entscheiden.
			 */
			setTab(canChooseRole ? TAB_KEYS.ROLE : TAB_KEYS.PERSON)
			return
		}
		// Aus der Spalte „Erinnerungen" (oder ohne Recht zum Bearbeiten)
		// beginnt das Fenster gleich bei den Erinnerungen.
		// Alumni-Konten haben keine Erinnerungen (keine Anforderungen, keine
		// Prüfungen) — dort beginnt das Fenster bei der Person bzw. den Kommentaren.
		const ohneErinnerungen = admin.role === TRoles.ALUMNI
		setTab(
			(options.tab === TAB_KEYS.REMINDERS || options.canEdit === false) && !ohneErinnerungen
				? TAB_KEYS.REMINDERS
				: options.canEdit === false
				? TAB_KEYS.COMMENTS
				: TAB_KEYS.PERSON
		)
		setId(admin.id || null)
		setValue('firstName', admin.firstName || '')
		setValue('lastName', admin.lastName || '')
		setValue('email', admin.email || '')
		setValue('state', admin.state || '')
		setValue('organizationName', admin.organizationName || '')
		setValue('salutation', admin.salutation || '')
		setValue('gender', admin.gender || '')
		setValue('role', admin.role || '')
		setValue('manageStateAdmins', admin?.permissions?.manageStateAdmins || false)
		setValue('manageDatabaseReset', admin?.permissions?.manageDatabaseReset || false)
		setValue('manageFederalAdmins', admin?.permissions?.manageFederalAdmins || false)
	}

	const handleClose = () => {
		reset()
		setId(null)
		setAdmin(null)
		setCanEdit(true)
		setRemindersDirty(false)
	}

	/*
	 * Ampel an der Reiterleiste: Ein Punkt erscheint nur, wo etwas beanstandet
	 * ist (`done: false`). Ein gruener Punkt an jedem fehlerfreien Reiter waere
	 * nur Farbe ohne Aussage — hier ist nichts abzuhaken, es sind Stammdaten.
	 */
	const tabError = key => (TAB_FIELDS[key].some(field => errors[field]) ? false : undefined)

	/*
	 * Die Reiterleiste. Der Reiter „Rolle & Rechte" steht nur dort, wo die Rolle
	 * auch gewaehlt werden darf — sonst waere er ein leerer Reiter.
	 */
	const tabs = [
		...(canEdit
			? [
					{ key: TAB_KEYS.PERSON, label: 'Person', done: tabError(TAB_KEYS.PERSON) },
					...(canChooseRole
						? [{ key: TAB_KEYS.ROLE, label: 'Role and rights', done: tabError(TAB_KEYS.ROLE) }]
						: []),
					{
						key: TAB_KEYS.RESPONSIBILITY,
						label: 'Responsibility',
						done: tabError(TAB_KEYS.RESPONSIBILITY)
					}
			  ]
			: []),
		// Erinnerungen und Verlauf gibt es nur zu einem bestehenden Konto.
		...(id
			? [
					...(admin?.role === TRoles.ALUMNI ? [] : [{ key: TAB_KEYS.REMINDERS, label: 'Reminders' }]),
					{ key: TAB_KEYS.COMMENTS, label: 'Comments' }
			  ]
			: [])
	]

	/*
	 * Ein beanstandetes Feld kann in einem zugeklappten Reiter stehen — dann
	 * passierte auf „Speichern" scheinbar nichts. Deshalb wird der erste Reiter
	 * mit Beanstandung geoeffnet. Gesucht wird nur unter den Reitern, die es
	 * hier tatsaechlich gibt: Wer die Rolle nicht waehlen darf, landete sonst
	 * auf einem Reiter, der gar nicht angezeigt wird.
	 */
	const onInvalid = invalid => {
		const target = tabs.find(({ key }) => TAB_FIELDS[key].some(field => invalid[field]))
		if (target) setTab(target.key)
	}

	const rolesOptions = [TRoles.ADMIN, TRoles.FEDERAL_ADMIN, TRoles.ALUMNI].map(item => ({
		label: item,
		value: item
	}))

	return (
		<BaseModal
			onClose={handleClose}
			onOpen={handleOpen}
			nameEvent={types.openAddAdmin}
			isDirty={Boolean(Object.keys(dirtyFields).length) || remindersDirty}
			title={!canEdit ? t('Email reminders') : id ? t('Edit administrator') : t('Add Administrator')}
			isLoading={isLoading}
			// Zwei Spalten machen das Fenster breiter und kürzer. Auf einem
			// niedrigen Bildschirm scrollt trotzdem der Rumpf und nicht die
			// Seite dahinter — sonst läge „Speichern" unter dem Rand.
			fitViewport
			maxWidth={840}
		>
			{({ closeModal }) => (
				<>
					{/*
					 * Der Kopf zeigt, wessen Zugang hier offen ist: Profilbild,
					 * Name, E-Mail, Rolle, Zustaendigkeit und die letzte
					 * Anmeldung. Beim Anlegen gibt es noch niemanden — dann
					 * bleibt er weg, statt ein Fragezeichen zu zeigen.
					 */}
					{admin && (
						<div className={styles.header}>
							<AuthAvatar
								hashName={admin.avatarHashName}
								alt=""
								className={styles.avatar}
								fallback={
									<Avatar name={name || '?'} size="52" round color="#c3edef" fgColor="#22404d" />
								}
							/>
							<div className={styles.identity}>
								<h2 className={styles.headerName}>{name}</h2>
								{admin.email && <span className={styles.mail}>{admin.email}</span>}
								<div className={styles.chips}>
									{selectedRole && (
										<span className={styles.chip}>
											{t(
												selectedRole === TRoles.FEDERAL_ADMIN
													? 'Federaladmin'
													: selectedRole === TRoles.ALUMNI
													? 'ALUMNI'
													: 'Stateadmin'
											)}
										</span>
									)}
									{/* Ein Bundesadmin hat kein Bundesland — dort steht
									    „Alle", so wie in der Spalte der Liste. */}
									<span className={clsx(styles.chip, styles.chipAffiliation)}>
										{admin.state ? stateLabel(admin.state) : t('All')}
									</span>
									{admin.organizationName && (
										<span className={clsx(styles.chip, styles.chipAffiliation)}>
											{admin.organizationName}
										</span>
									)}
									{admin.lastLoginAt && (
										<span className={clsx(styles.chip, styles.chipMuted)}>
											{t('Last sign-in')}: {formatVienna(admin.lastLoginAt, 'dd.MM.yyyy HH:mm')}
										</span>
									)}
								</div>
							</div>
						</div>
					)}

					<TabBar className={styles.tabs} tabs={tabs} active={tab} onChange={setTab} tabsRef={tabsRef} />

					{/* Die Reiter teilen sich EIN Formular und werden ausgeblendet,
					    nicht ausgehaengt: Ausgehaengt verloere react-hook-form die
					    eingetippten Werte, und „Speichern" schickt weiterhin alle
					    Felder auf einmal — egal, in welchem Reiter sie stehen. */}
					<form
						className={clsx(styles.root, !canEdit && styles.hidden)}
						onSubmit={handleSubmit(
							form =>
								onSubmit(form, () => {
									closeModal()
									setId(null)
								}),
							onInvalid
						)}
					>
						{canChooseRole && (
							<section className={clsx(styles.section, tab !== TAB_KEYS.ROLE && styles.hidden)}>
								<div className={styles.grid}>
									<SelectField
										menuPortal
										label={t('Admin roles')}
										className={styles.field}
										control={control}
										error={errors.role}
										valuesToOptions
										options={rolesOptions.map(v => ({
											label: t(v.value),
											value: v.value
										}))}
										name="role"
										placeholder={t('Select admin role')}
										required={true}
									/>
									{/*
									 * Der Hinweis erklärt die gewählte Rolle. Solange
									 * keine gewählt ist, sagt er, was zur Wahl steht —
									 * ein Satz über den Landesadmin stünde dort sonst
									 * als Aussage über etwas, das niemand gewählt hat.
									 */}
									<p className={styles.roleHint}>
										{!selectedRole
											? t('State admin for one federal state, federal admin Austria-wide.')
											: selectedRole === TRoles.FEDERAL_ADMIN
											? t(
													'A federal admin works Austria-wide and can additionally be given the functions below.'
											  )
											: selectedRole === TRoles.ALUMNI
											? t('alumniRoleHint')
											: t('A state admin looks after one federal state and sees only its data.')}
									</p>
								</div>

								{selectedRole === TRoles.FEDERAL_ADMIN && (
									<div className={styles.permissionsSection}>
										<p className={styles.permissionsIntro}>
											{t(
												'Additional functions only exist for federal admins. Without a tick the person sees the administration, but changes nothing there.'
											)}
										</p>
										{PERMISSIONS.map(permission => (
											<div className={styles.permission} key={permission.name}>
												<Checkbox
													label={t(permission.label)}
													register={register(permission.name)}
												/>
												<p className={styles.permissionHint}>{t(permission.hint)}</p>
											</div>
										))}
									</div>
								)}
							</section>
						)}

						<section className={clsx(styles.section, tab !== TAB_KEYS.PERSON && styles.hidden)}>
							<div className={styles.grid}>
								<SelectField
									menuPortal
									className={styles.field}
									required
									label={t('salutation')}
									error={errors.salutation}
									placeholder={t('Select salutation')}
									options={props.salutations?.map(v => ({
										value: v.value_EN,
										label: i18n.language === TLanguages.EN ? v.value_EN : v.value_DE
									}))}
									control={control}
									name="salutation"
								/>
								<SelectField
									menuPortal
									className={styles.field}
									required
									label={t('gender')}
									error={errors.gender}
									placeholder={t('Select gender')}
									options={genderOption
										.map(v => ({ label: t(v.label), value: v.value }))
										.sort((a, b) => a.label.localeCompare(b.label))}
									control={control}
									name="gender"
								/>
								<TextField
									className={styles.field}
									required
									label={t('First Name')}
									register={register('firstName')}
									error={errors.firstName}
									placeholder={t('Type First Name')}
								/>
								<TextField
									className={styles.field}
									required
									label={t('Last Name')}
									register={register('lastName')}
									error={errors.lastName}
									placeholder={t('Type Last Name')}
								/>
								<TextField
									className={styles.field}
									disabled={id}
									required
									label={t('Email')}
									register={register('email')}
									error={errors.email}
									placeholder={t('Email')}
								/>
								{/*
								 * Das Passwort steht bewusst bei der Person und nicht
								 * bei den Rechten — es wird nur beim Bearbeiten
								 * gebraucht, beim Anlegen schickt das System eines zu.
								 */}
								{id && (
									<TextField
										className={styles.field}
										type="password"
										label={t('New password')}
										placeholder={t('New password')}
										error={errors.password}
										register={register('password')}
									/>
								)}
							</div>
						</section>

						<section className={clsx(styles.section, tab !== TAB_KEYS.RESPONSIBILITY && styles.hidden)}>
							<div className={styles.grid}>
								<TextField
									className={styles.field}
									required={selectedRole === TRoles.ADMIN}
									label={t('Organization name')}
									register={register('organizationName')}
									error={errors.organizationName}
									placeholder={t('Organization name')}
								/>
								{/*
								 * Ein Bundesadmin ist für ganz Österreich zuständig;
								 * beim Speichern wird sein Bundesland ohnehin geleert.
								 * Ein Feld, das nichts bewirkt, steht deshalb gar
								 * nicht erst da.
								 */}
								{NATIONWIDE_ROLES.includes(selectedRole) ? (
									<p className={styles.roleHint}>
										{t(
											selectedRole === TRoles.ALUMNI
												? 'alumniResponsibilityHint'
												: 'Responsible Austria-wide — no federal state.'
										)}
									</p>
								) : (
									<SelectField
										menuPortal
										className={styles.field}
										required={selectedRole === TRoles.ADMIN}
										label={t('state')}
										error={errors.state}
										placeholder={t('state')}
										options={StateOptions.map(v => ({
											label: v.label,
											value: v.value
										})).sort((a, b) => a.label.localeCompare(b.label))}
										control={control}
										name="state"
									/>
								)}
							</div>
						</section>

						<Btn
							loading={isLoading}
							width={155}
							className={clsx(styles.btnSubmit, !FORM_TABS.includes(tab) && styles.hidden)}
							type="submit"
							icon="send"
							iconSize={{ w: 15, h: 15 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</form>

					{/*
					 * Erinnerungen per E-Mail: eigene Komponente mit eigenem
					 * Speichern-Knopf (dieselbe wie unter „Mein Profil").
					 * Ausgeblendet statt ausgehängt, damit Ungespeichertes beim
					 * Reiterwechsel stehen bleibt.
					 */}
					{/* Alumni-Konten haben keine Erinnerungen — ohne die Bedingung
					    fragte das ausgeblendete Formular sie trotzdem ab (404). */}
					{id && admin?.role !== TRoles.ALUMNI && (
						<section className={clsx(styles.section, tab !== TAB_KEYS.REMINDERS && styles.hidden)}>
							<p className={styles.remindersHint}>{t('remindersOtherHint')}</p>
							<ReminderSettings
								userId={id}
								role={admin?.role}
								onDirtyChange={setRemindersDirty}
								onSaved={() => onDone && onDone()}
							/>
						</section>
					)}

					{/* Bei jedem Öffnen neu geholt — so steht eine eben
					    gespeicherte Änderung der Erinnerungen gleich darin. */}
					{id && tab === TAB_KEYS.COMMENTS && (
						<section className={styles.section}>
							<RecordComments entityType={RECORD_TYPES.USER} entityId={id} />
						</section>
					)}
				</>
			)}
		</BaseModal>
	)
}
AddAdmin.propTypes = {
	salutations: PropTypes.array,
	genders: PropTypes.array
}
const mapStateToProps = state => ({
	salutations: state.options.salutations,
	genders: state.options.genders,
	permissions: state.user.profile.permissions
})
export default connect(mapStateToProps)(AddAdmin)
